import {
  createDatabaseBlockDataSource,
  type DatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import type { Store } from '@blocksuite/store';
import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import type { WorkspaceAreaCacheStore } from '../../folder-api';
import type { WorkspaceAreaRef } from '../../folder-api/services/workspace-area-api';
import type {
  AccessibleFolderCacheStore,
  AccessibleFolderMeta,
} from '../../organize';
import { waitForDatabaseStorageReady } from '../services/cross-doc-automation';
import type {
  ViewBackedAppAreaRef,
  ViewBackedAppDescriptor,
  ViewBackedAppSourceScope,
} from './types';

interface OpenedViewBackedDoc {
  release: () => void;
  store: Store;
}

export interface LocalDatabaseView {
  area: ViewBackedAppAreaRef | null;
  blockId: string;
  dataSource: DatabaseBlockDataSource | null;
  databaseId: string;
  docId: string;
  diagnostics: unknown | null;
  model: DatabaseBlockModel;
  projectionError: string | null;
  title: string;
  view: unknown | null;
  viewData: { id: string; mode: string };
  viewId: string;
}

type LocalDatabaseSingleView = {
  type?: string;
} & Partial<Record<'name$', { value?: string | null }>>;

export interface DatabaseViewLocalDiscoveryInput {
  descriptor: ViewBackedAppDescriptor;
  onDiagnostic?: (diagnostic: DatabaseViewLocalDiscoveryDiagnostic) => void;
  pendingAreaByDocId?: ReadonlyMap<string, string>;
}

export interface DatabaseViewLocalDiscoveryDiagnostic {
  databaseId?: string;
  docId: string;
  error: unknown;
  kind: 'database-read-failed' | 'doc-open-failed' | 'doc-project-failed';
  viewMode: string;
}

export class DatabaseViewLocalDiscoveryService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly workspaceAreaCache: WorkspaceAreaCacheStore,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore
  ) {
    super();
  }

  async listLocalViews(
    input: DatabaseViewLocalDiscoveryInput
  ): Promise<LocalDatabaseView[]> {
    const views: LocalDatabaseView[] = [];
    const docIds = [...this.docsService.list.nonTrashDocsIds$.value];
    const workspaceAreas = this.workspaceAreaCache.getCachedAreas();
    const folders = this.accessibleFolderCache.getCachedFolders();

    for (const docId of docIds) {
      const opened = await this.openDoc(docId).catch(error => {
        this.reportDiagnostic(input.onDiagnostic, {
          docId,
          error,
          kind: 'doc-open-failed',
          viewMode: input.descriptor.viewMode,
        });
        console.warn('[DatabaseViewLocalDiscovery] Failed to open doc', {
          docId,
          error,
        });
        return null;
      });
      if (!opened) continue;
      try {
        const area = this.resolveAreaForDoc({
          docId,
          explicitRootFolderId: input.pendingAreaByDocId?.get(docId),
          folders,
          workspaceAreas,
        });
        views.push(
          ...(await this.findViewsInStore(
            opened.store,
            input.descriptor,
            area,
            input.onDiagnostic
          ))
        );
      } catch (error) {
        this.reportDiagnostic(input.onDiagnostic, {
          docId,
          error,
          kind: 'doc-project-failed',
          viewMode: input.descriptor.viewMode,
        });
        console.warn('[DatabaseViewLocalDiscovery] Failed to project doc', {
          docId,
          error,
        });
      } finally {
        opened.release();
      }
    }

    return views.sort(compareLocalViews);
  }

  async findLocalView(input: {
    databaseId: string;
    descriptor: ViewBackedAppDescriptor;
    store: Store;
    viewId: string;
  }): Promise<LocalDatabaseView | null> {
    const views = await this.findViewsInStore(input.store, input.descriptor);
    return (
      views.find(
        view =>
          view.databaseId === input.databaseId && view.viewId === input.viewId
      ) ?? null
    );
  }

  async openDoc(docId: string): Promise<OpenedViewBackedDoc> {
    const ref = this.docsService.open(docId);
    try {
      ref.doc.blockSuiteDoc.load();
      await ref.doc.waitForSyncReady();
      return {
        release: ref.release,
        store: ref.doc.blockSuiteDoc,
      };
    } catch (error) {
      ref.release();
      throw error;
    }
  }

  private async findViewsInStore(
    store: Store,
    descriptor: ViewBackedAppDescriptor,
    area: ViewBackedAppAreaRef | null = null,
    onDiagnostic?: (diagnostic: DatabaseViewLocalDiscoveryDiagnostic) => void
  ): Promise<LocalDatabaseView[]> {
    const databases = store.getBlocksByFlavour('affine:database');
    const result: LocalDatabaseView[] = [];
    for (const block of databases) {
      const model = block.model as DatabaseBlockModel | undefined;
      if (!model) continue;
      const blockId = databaseBlockId(block, model);
      const databaseId = databaseIdForModel(model, blockId);
      try {
        const dataSource = await this.createReadyDataSource(model, descriptor);
        for (const viewData of dataSource.viewDataList$.value) {
          if (viewData.mode !== descriptor.viewMode) continue;
          const base = {
            area,
            blockId,
            dataSource,
            databaseId,
            diagnostics: null,
            docId: store.id,
            model,
            title:
              databaseTitle(model) ||
              this.docsService.list.doc$(store.id).value?.title$.value ||
              descriptor.defaultTitle,
            viewData: { id: viewData.id, mode: viewData.mode },
            viewId: viewData.id,
          };
          try {
            const view = dataSource.viewManager.viewGet(
              viewData.id
            ) as LocalDatabaseSingleView | null;
            if (!view || view.type !== descriptor.viewMode) {
              result.push({
                ...base,
                diagnostics: null,
                projectionError: 'view-diagnostics-unavailable',
                view: null,
              });
              continue;
            }
            const diagnostics = descriptor.readDiagnostics({ view });
            if (!diagnostics) {
              result.push({
                ...base,
                diagnostics: null,
                projectionError: 'view-diagnostics-unavailable',
                title: view['name$']?.value || base.title,
                view: null,
              });
              continue;
            }
            result.push({
              ...base,
              diagnostics,
              projectionError: null,
              title: view['name$']?.value || base.title,
              view,
            });
          } catch (error) {
            console.warn(
              '[DatabaseViewLocalDiscovery] Failed to read database view',
              {
                databaseId: model.id,
                error,
                viewId: viewData.id,
                viewMode: descriptor.viewMode,
              }
            );
            result.push({
              ...base,
              diagnostics: null,
              projectionError: 'projection-failed',
              view: null,
            });
          }
        }
      } catch (error) {
        this.reportDiagnostic(onDiagnostic, {
          databaseId,
          docId: store.id,
          error,
          kind: 'database-read-failed',
          viewMode: descriptor.viewMode,
        });
        console.warn('[DatabaseViewLocalDiscovery] Failed to read database', {
          databaseId: model.id,
          error,
          viewMode: descriptor.viewMode,
        });
        for (const viewData of rawViewDataList(model, descriptor)) {
          result.push({
            area,
            blockId,
            dataSource: null,
            databaseId,
            diagnostics: null,
            docId: store.id,
            model,
            projectionError: 'projection-failed',
            title:
              rawViewTitle(viewData) ||
              databaseTitle(model) ||
              this.docsService.list.doc$(store.id).value?.title$.value ||
              descriptor.defaultTitle,
            view: null,
            viewData,
            viewId: viewData.id,
          });
        }
      }
    }
    return result;
  }

  private async createReadyDataSource(
    model: DatabaseBlockModel,
    descriptor: ViewBackedAppDescriptor
  ): Promise<DatabaseBlockDataSource> {
    if (!(await waitForDatabaseStorageReady(model))) {
      throw new Error(
        `${descriptor.appKind} database storage for "${model.id}" did not become ready.`
      );
    }
    return createDatabaseBlockDataSource(model);
  }

  private reportDiagnostic(
    onDiagnostic: DatabaseViewLocalDiscoveryInput['onDiagnostic'],
    diagnostic: DatabaseViewLocalDiscoveryDiagnostic
  ): void {
    onDiagnostic?.(diagnostic);
  }

  private resolveAreaForDoc(input: {
    docId: string;
    explicitRootFolderId?: string;
    folders: readonly AccessibleFolderMeta[];
    workspaceAreas: readonly WorkspaceAreaRef[];
  }): ViewBackedAppAreaRef | null {
    if (input.explicitRootFolderId) {
      return (
        input.workspaceAreas
          .filter(area => area.rootFolderId === input.explicitRootFolderId)
          .map(toViewBackedAreaRef)[0] ?? null
      );
    }

    const rootFolderId =
      this.accessibleFolderCache.getCachedWorkspaceRootFolderId();
    if (
      rootFolderId &&
      this.accessibleFolderCache
        .getCachedRootFolderDocIds()
        .includes(input.docId)
    ) {
      return this.areaForFolder(rootFolderId, input);
    }

    const containingFolder = input.folders.find(folder =>
      folder.docIds.includes(input.docId)
    );
    if (!containingFolder) return null;
    return this.areaForFolder(containingFolder.id, input);
  }

  private areaForFolder(
    folderId: string,
    input: {
      folders: readonly AccessibleFolderMeta[];
      workspaceAreas: readonly WorkspaceAreaRef[];
    }
  ): ViewBackedAppAreaRef | null {
    const folderById = new Map(
      input.folders.map(folder => [folder.id, folder])
    );
    let currentId: string | null = folderId;
    const visited = new Set<string>();
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const area = input.workspaceAreas.find(
        area => area.rootFolderId === currentId
      );
      if (area) return toViewBackedAreaRef(area);
      currentId = folderById.get(currentId)?.parentId ?? null;
    }
    return null;
  }
}

export function toViewBackedAreaRef(
  area: WorkspaceAreaRef | ViewBackedAppAreaRef
): ViewBackedAppAreaRef {
  if ('systemFolderId' in area) return area;
  return {
    kind: area.kind,
    name: area.name,
    ownerUserId: area.ownerUserId,
    rootFolderId: area.rootFolderId,
    systemFolderId: null,
    workspaceId: area.workspaceId,
  };
}

export function viewBackedAreaScope(
  area: Pick<ViewBackedAppAreaRef, 'kind'> | null
): ViewBackedAppSourceScope {
  return area?.kind === 'private' ? 'private' : 'shared';
}

function databaseTitle(model: DatabaseBlockModel): string {
  const title = model.props.title;
  if (typeof title === 'string') return title;
  if (title && typeof title.toString === 'function') {
    return title.toString();
  }
  return '';
}

function databaseBlockId(
  block: { id?: unknown },
  model: DatabaseBlockModel
): string {
  return typeof block.id === 'string' && block.id.length > 0
    ? block.id
    : model.id;
}

function databaseIdForModel(
  model: DatabaseBlockModel,
  blockId: string
): string {
  const props = model.props as {
    databaseId?: unknown;
  };
  return typeof props.databaseId === 'string' && props.databaseId.length > 0
    ? props.databaseId
    : blockId;
}

function rawViewDataList(
  model: DatabaseBlockModel,
  descriptor: ViewBackedAppDescriptor
): { id: string; mode: string }[] {
  const raw = (model as { dataSource?: unknown }).dataSource;
  const value = (
    raw as Partial<Record<'viewDataList$', { value?: unknown }>>
  )?.['viewDataList$']?.value;
  const items = Array.isArray(value)
    ? value
    : value &&
        typeof (value as Iterable<unknown>)[Symbol.iterator] === 'function'
      ? [...(value as Iterable<unknown>)]
      : [];
  return items.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const id = (item as { id?: unknown }).id;
    const mode = (item as { mode?: unknown }).mode;
    if (
      typeof id !== 'string' ||
      typeof mode !== 'string' ||
      mode !== descriptor.viewMode
    ) {
      return [];
    }
    const name = (item as { name?: unknown }).name;
    return [
      {
        id,
        mode,
        ...(typeof name === 'string' ? { name } : {}),
      },
    ];
  });
}

function rawViewTitle(viewData: { id: string; mode: string }): string {
  const name = (viewData as { name?: unknown }).name;
  return typeof name === 'string' ? name : '';
}

function compareLocalViews(a: LocalDatabaseView, b: LocalDatabaseView): number {
  return a.title.localeCompare(b.title) || a.viewId.localeCompare(b.viewId);
}
